import { db } from './firebase';
import { 
  collection, 
  getDocs, 
  getDoc, 
  doc, 
  addDoc, 
  setDoc, 
  deleteDoc, 
  query, 
  limit as fsLimit 
} from 'firebase/firestore';

const getApiBase = () => {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  if (typeof window !== 'undefined' && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
    return `${window.location.origin}/api`;
  }
  return 'http://localhost:4000/api';
};

const API_BASE = getApiBase();
const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD || 'admin123';

function getAdminAuthHeaders() {
  const pass = sessionStorage.getItem('admin_password') || ADMIN_PASSWORD;
  return {
    'Content-Type': 'application/json',
    'x-admin-password': pass,
    'Authorization': `Bearer ${pass}`
  };
}

/**
 * Fetch overview metrics across core collections
 */
export async function getAdminOverview() {
  try {
    const res = await fetch(`${API_BASE}/admin/overview`, {
      headers: getAdminAuthHeaders()
    });
    if (res.ok) {
      const json = await res.json();
      if (json.ok) return json.data;
    }
  } catch (e) {
    console.warn('Backend overview endpoint unreachable, falling back to Firebase client SDK', e);
  }

  // Fallback to client-side Firestore queries
  const collections = ['bookings', 'lead_magnets', 'testimonials', 'contacts', 'fna_reports', 'reports', 'tool_usage'];
  const counts = {};
  const recentActivity = [];

  await Promise.all(
    collections.map(async (colName) => {
      try {
        const snap = await getDocs(query(collection(db, colName), fsLimit(50)));
        counts[colName] = snap.size;

        snap.docs.slice(0, 3).forEach((d) => {
          const data = d.data();
          recentActivity.push({
            id: d.id,
            collection: colName,
            title: data.firstname ? `${data.firstname} ${data.lastname || ''}` : data.email || data.name || d.id,
            createdAt: data.createdAt || data.timestamp || new Date().toISOString()
          });
        });
      } catch (err) {
        counts[colName] = 0;
      }
    })
  );

  return { counts, recentActivity };
}

/**
 * List documents in a collection
 */
export async function getCollectionDocs(colName, maxLimit = 100) {
  try {
    const res = await fetch(`${API_BASE}/admin/collections/${colName}?limit=${maxLimit}`, {
      headers: getAdminAuthHeaders()
    });
    if (res.ok) {
      const json = await res.json();
      if (json.ok) return json.data.documents;
    }
  } catch (e) {
    console.warn(`Backend collection endpoint unreachable for ${colName}, falling back to Firestore client SDK`, e);
  }

  // Fallback to client SDK
  const snap = await getDocs(query(collection(db, colName), fsLimit(maxLimit)));
  return snap.docs.map((d) => ({
    _id: d.id,
    ...d.data()
  }));
}

/**
 * Create document in a collection
 */
export async function createCollectionDoc(colName, docData) {
  try {
    const res = await fetch(`${API_BASE}/admin/collections/${colName}`, {
      method: 'POST',
      headers: getAdminAuthHeaders(),
      body: JSON.stringify(docData)
    });
    if (res.ok) {
      const json = await res.json();
      if (json.ok) return json.data;
    }
  } catch (e) {
    console.warn(`Backend create endpoint failed for ${colName}, falling back to Firestore client SDK`, e);
  }

  // Fallback to client SDK
  const docRef = await addDoc(collection(db, colName), {
    ...docData,
    createdAt: docData.createdAt || new Date().toISOString()
  });
  return { _id: docRef.id, ...docData };
}

/**
 * Update document in a collection
 */
export async function updateCollectionDoc(colName, docId, updateData) {
  try {
    const res = await fetch(`${API_BASE}/admin/collections/${colName}/${docId}`, {
      method: 'PUT',
      headers: getAdminAuthHeaders(),
      body: JSON.stringify(updateData)
    });
    if (res.ok) {
      const json = await res.json();
      if (json.ok) return json.data;
    }
  } catch (e) {
    console.warn(`Backend update endpoint failed for ${colName}/${docId}, falling back to Firestore client SDK`, e);
  }

  // Fallback to client SDK
  const docRef = doc(db, colName, docId);
  const cleanData = { ...updateData };
  delete cleanData._id;

  await setDoc(docRef, {
    ...cleanData,
    updatedAt: new Date().toISOString()
  }, { merge: true });

  return { _id: docId, ...cleanData };
}

/**
 * Delete document in a collection
 */
export async function deleteCollectionDoc(colName, docId) {
  try {
    const res = await fetch(`${API_BASE}/admin/collections/${colName}/${docId}`, {
      method: 'DELETE',
      headers: getAdminAuthHeaders()
    });
    if (res.ok) {
      const json = await res.json();
      if (json.ok) return json.data;
    }
  } catch (e) {
    console.warn(`Backend delete endpoint failed for ${colName}/${docId}, falling back to Firestore client SDK`, e);
  }

  // Fallback to client SDK
  const docRef = doc(db, colName, docId);
  await deleteDoc(docRef);
  return { deletedId: docId };
}

/**
 * Trigger Custom Branded Email to a user
 */
export async function triggerCustomEmail({ recipientEmail, subject, bodyHtml, bodyText }) {
  const res = await fetch(`${API_BASE}/admin/triggers/custom-email`, {
    method: 'POST',
    headers: getAdminAuthHeaders(),
    body: JSON.stringify({ recipientEmail, subject, bodyHtml, bodyText })
  });

  const json = await res.json();
  if (!res.ok || !json.ok) {
    throw new Error(json.error || 'Failed to send custom email');
  }
  return json;
}

/**
 * Trigger Lead Magnet Delivery Email
 */
export async function triggerLeadMagnetEmail({ email, firstname, lastname, docId }) {
  return triggerCustomEmail({
    recipientEmail: email,
    subject: 'Your Comprehensive Financial Formula & Strategy Guide 📄',
    bodyHtml: `
      <p style="margin-bottom: 16px;">Hello ${firstname || 'Valued Client'},</p>
      <p style="margin-bottom: 16px;">
        Here is your copy of our <strong>Comprehensive Financial Formula & Strategy Guide</strong>!
      </p>
      <p style="margin-bottom: 20px;">
        Ready to take control of your financial future? Book a 1-on-1 strategy session with Priyanka Karn.
      </p>
      <div style="text-align: center; margin: 24px 0;">
        <a href="https://www.growandstayrich.com/#booking" style="background-color: #c9a84c; color: #070e1b; padding: 12px 24px; border-radius: 6px; font-weight: bold; text-decoration: none; display: inline-block;">
          📅 Book Your 1-on-1 Strategy Session
        </a>
      </div>
    `
  });
}

/**
 * Trigger Strategy Session Booking Confirmation Email
 */
export async function triggerBookingEmail({ email, firstname, meeting_preferred_date, preferred_time, consultation_type, meetLink }) {
  const meetUrl = meetLink || 'https://meet.google.com/piw-qsyu-fqg';
  return triggerCustomEmail({
    recipientEmail: email,
    subject: `Confirmed: Strategy Session on ${meeting_preferred_date || 'your selected date'}`,
    bodyHtml: `
      <h2 style="color: #f8fafc; margin-top: 0;">Your Consultation is Confirmed!</h2>
      <p style="color: #cbd5e1; line-height: 1.6;">
        Hello ${firstname || ''}, thank you for booking a strategy session with us. We look forward to meeting with you!
      </p>
      <div style="background-color: #0d1b2a; padding: 18px; border-radius: 8px; margin: 20px 0; border: 1px solid #334155;">
        <p style="margin: 4px 0; color: #f8fafc;"><strong>📅 Date:</strong> ${meeting_preferred_date || 'N/A'}</p>
        <p style="margin: 4px 0; color: #f8fafc;"><strong>⏰ Time:</strong> ${preferred_time || 'N/A'} (Central Time - CT)</p>
        <p style="margin: 4px 0; color: #f8fafc;"><strong>📋 Type:</strong> ${consultation_type || 'Initial Consultation'}</p>
        <p style="margin: 14px 0 4px 0;">
          <a href="${meetUrl}" style="background-color: #c9a84c; color: #070e1b; padding: 12px 20px; border-radius: 6px; font-weight: bold; text-decoration: none; display: inline-block;">🎥 Join Google Meet Call</a>
        </p>
      </div>
    `
  });
}
